import { IVector3 } from '@seddi/math-v2';
import { OrientedSplineMesh } from '@seddi/scene';
import { Mesh, MeshStandardMaterial, Object3D, SRGBColorSpace } from '@seddi/three';

import { create as createGeometry } from '../core/bufferGeometry';
import { ResourcesContext } from '../resources/resourcesContext';

export interface StitchRenderable {
  material: MeshStandardMaterial;
  mesh: Mesh;
}

/**
 * Convert color to HEX string.
 * @param value Color in sRGB space.
 */
const srgbToHex = (value: IVector3): string => {
  return (
    '#' +
    [Math.ceil(value.x * 255), Math.ceil(value.y * 255), Math.ceil(value.z * 255)]
      .map((x) => {
        const hex = x.toString(16);
        return hex.length === 1 ? '0' + hex : hex;
      })
      .join('')
  );
};

/**
 * Unique stitch material name by color.
 * @param color - Color in sRGB space.
 */
const createMaterialName = (color: IVector3): string => {
  return `stitch_${srgbToHex(color)}`;
};

/**
 * Create stitch material.
 * @param color - Color in sRGB space.
 */
const createMaterial = (color: IVector3, context: ResourcesContext): MeshStandardMaterial => {
  const key = createMaterialName(color);
  // Reuse stitch materials by color.
  if (context.materialsRegistry.has(key)) {
    return context.materialsRegistry.get(key) as MeshStandardMaterial;
  }
  // Create stitch material by color.
  const material = new MeshStandardMaterial();
  material.color.setRGB(color.x, color.y, color.z, SRGBColorSpace);
  material.roughness = 0.9;
  context.materialsRegistry.set(key, material);
  return material;
};

/**
 * Create renderable.
 * @param geometryData - Stitch geometry data.
 * @param color - Color in sRGB space.
 * @param context - Renderer context.
 */
export const create = (
  parent: Object3D,
  geometryData: OrientedSplineMesh,
  color: IVector3,
  context: ResourcesContext,
): StitchRenderable => {
  const material = createMaterial(color, context);
  // TODO: this is calling computeBoundingSphere
  const geometry = createGeometry(geometryData);
  const mesh = new Mesh(geometry, material);
  parent.add(mesh);
  // TODO: Material reference counting.
  return { material, mesh };
};

/**
 * Dispose renderable.
 * @param renderable - Stitch renderable.
 */
export const dispose = (renderable: StitchRenderable) => {
  const mesh = renderable.mesh;
  mesh.geometry.dispose();
  mesh.parent.remove(mesh);
};

/**
 * Update renderable with new color.
 * @param renderable - Stitch renderable.
 * @param color - Color in sRGB space.
 * @param context - Renderer context.
 */
export const updateColor = (
  renderable: StitchRenderable,
  color: IVector3,
  context: ResourcesContext,
) => {
  const material = createMaterial(color, context);
  renderable.mesh.material = material;
  // TODO: Material reference counting.
};
